import { EDocumentPageStatus } from '@if/core/types'
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material'
import { alpha, Badge, Button, Checkbox, ImageListItemBar, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { ChangeEventHandler, MouseEventHandler, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButtonWithTooltip } from '../../../components'
import { DocumentPageDisplayInfo } from '../../../data/types/DocumentPageDisplayInfo'

interface Props {
  page: DocumentPageDisplayInfo
  onRotatePage: MouseEventHandler<HTMLButtonElement>
  onDeletePage: ChangeEventHandler<HTMLInputElement>
  getTitle: (page: DocumentPageDisplayInfo) => string
  onImageClicked: MouseEventHandler<HTMLButtonElement>
  showBanners: boolean
  index: number
  selectedPages: string[]
  isDraggable: boolean
  isActionDisabled: boolean
}

const Thumbnail = ({
  page,
  onRotatePage,
  onDeletePage,
  getTitle,
  onImageClicked,
  showBanners,
  index,
  selectedPages,
  isDraggable,
  isActionDisabled,
}: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [thumbnailBlob, setThumbnailBlob] = useState<string>('')
  const fetchedUrlsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const token = localStorage.getItem('jwtToken') // or your token key
    if (page.src && !fetchedUrlsRef.current.has(page.src)) {
      fetchedUrlsRef.current.add(page.src)
      fetch(page.src, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.blob())
        .then((blob) => {
          setThumbnailBlob(URL.createObjectURL(blob))
        })
        .catch(() => {
          void setThumbnailBlob('')
        })
    }
  }, [page.src])

  return (
    <div style={{ width: 'auto', height: 'auto' }}>
      {isDraggable && (
        <img
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'fill',
            transform: `rotate(${page.rotation}deg)`,
          }}
          src={thumbnailBlob}
          srcSet={thumbnailBlob}
          alt="Loading"
          loading="lazy"
        />
      )}
      {!isDraggable && (
        <Button
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            cursor: 'pointer',
            p: 0,
          }}
          id={page.id}
          disabled={isActionDisabled}
          onClick={onImageClicked}
        >
          <img
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              transform: `rotate(${page.rotation}deg)`,
            }}
            src={thumbnailBlob}
            srcSet={thumbnailBlob}
            alt="Loading"
            loading="lazy"
          />
        </Button>
      )}
      {showBanners && (
        <div>
          <ImageListItemBar
            title={
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <IconButtonWithTooltip
                  tooltip={t('document.rotateLeft')}
                  icon={<RotateLeftIcon sx={{ color: 'rgba(255, 255, 255, 0.54)' }} />}
                  aria-label="rotateLeft"
                  aria-rowindex={index}
                  aria-valuenow={page.rotation}
                  onClick={onRotatePage}
                  disabled={isActionDisabled}
                />

                <Checkbox
                  checked={page.pageStatus === EDocumentPageStatus.Deleted}
                  color="error"
                  icon={<DeleteOutlineIcon />}
                  checkedIcon={<DeleteIcon />}
                  value={index}
                  name={page.pageStatus}
                  onChange={onDeletePage}
                  disabled={isActionDisabled}
                />

                <IconButtonWithTooltip
                  tooltip={t('document.rotateRight')}
                  icon={<RotateRightIcon sx={{ color: 'rgba(255, 255, 255, 0.54)' }} />}
                  aria-label="rotateRight"
                  aria-rowindex={index}
                  aria-valuenow={page.rotation}
                  onClick={onRotatePage}
                  disabled={isActionDisabled}
                />

                <Typography variant="body2" position="absolute" right={0} marginRight="0.5rem" marginTop="2.5rem">
                  {`${page.pageNumber}`}
                </Typography>
              </Stack>
            }
          />
          <ImageListItemBar
            sx={[
              {
                cursor: 'pointer',
              },
              page.pageStatus === EDocumentPageStatus.Deleted
                ? {
                    backgroundColor: alpha(theme.palette.error.main, 0.5),
                  }
                : {
                    backgroundColor: '',
                  },
            ]}
            title={
              <Tooltip title={<span>{getTitle(page)}</span>} arrow placement="top">
                <span> {getTitle(page)}</span>
              </Tooltip>
            }
            position="top"
            actionIcon={
              <Stack direction="row" justifyContent="center">
                <div>
                  {page.pageStatus === EDocumentPageStatus.New && (
                    <Badge color="primary" badgeContent="NEW" sx={{ mr: 3 }} />
                  )}
                </div>

                <div>
                  {selectedPages.findIndex((x) => x === page.id) !== -1 && (
                    <Badge
                      color="primary"
                      badgeContent={selectedPages.findIndex((x) => x === page.id) + 1}
                      sx={{ mr: 2 }}
                    >
                      <CheckCircleIcon color="primary" />
                    </Badge>
                  )}
                </div>
              </Stack>
            }
          />
        </div>
      )}
    </div>
  )
}

export default Thumbnail
